import React, { useState } from "react";
import { Mail, MessageCircle, Instagram, Facebook } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CONTACT_EMAIL, FACEBOOK_URL, INSTAGRAM_URL, whatsappLink } from "@/lib/config";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "", website: "" });
    } catch {
      toast.error("Error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-champagne mb-4">Kaós®</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-charcoal">{t("contact.title")}</h1>
        <p className="text-stone-500 mt-4 max-w-md">{t("contact.subtitle")}</p>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-16">
        <Reveal>
          <form onSubmit={submit} className="space-y-6">
            <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("contact.name")}</label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
                className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("contact.email")}</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email"
                className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("contact.message")}</label>
              <Textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="rounded-none bg-charcoal text-white hover:bg-stone-800 h-12 px-10 text-xs uppercase tracking-[0.2em]"
            >
              {t("contact.submit")}
            </Button>
          </form>
        </Reveal>

        <Reveal delay={150}>
          <div className="bg-champagne-soft p-6 sm:p-10 md:p-14 h-full min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-stone-600 mb-8">{t("contact.info.title")}</p>
            <div className="space-y-6">
              <div className="flex items-start gap-4 min-w-0">
                <Mail size={18} strokeWidth={1.5} className="text-charcoal mt-1 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-stone-500">Email</p>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="block text-charcoal [overflow-wrap:anywhere] hover:text-champagne transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MessageCircle size={18} strokeWidth={1.5} className="text-charcoal mt-1 shrink-0" />
                <div>
                  <p className="text-sm text-stone-500">WhatsApp</p>
                  <a href={whatsappLink()} target="_blank" rel="noreferrer" className="text-charcoal hover:text-champagne transition-colors">
                    +39 380 759 1108
                  </a>
                </div>
              </div>
              {INSTAGRAM_URL && <div className="flex items-start gap-4">
                <Instagram size={18} strokeWidth={1.5} className="text-charcoal mt-1 shrink-0" />
                <div>
                  <p className="text-sm text-stone-500">Social</p>
                  <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="text-charcoal hover:text-champagne transition-colors">
                    Instagram
                  </a>
                </div>
              </div>}
              {FACEBOOK_URL && <div className="flex items-start gap-4">
                <Facebook size={18} strokeWidth={1.5} className="text-charcoal mt-1 shrink-0" />
                <div>
                  <p className="text-sm text-stone-500">Social</p>
                  <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="text-charcoal hover:text-champagne transition-colors">
                    Facebook
                  </a>
                </div>
              </div>}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Contact;
